import { Box, ButtonBase, type SxProps, type Theme } from '@mui/material';
import type { ReactNode } from 'react';

/**
 * Base pill component for the AV logic card.
 * Matches ConceptGroupChip visual style.
 *
 * - When `onClick` is provided: renders as interactive ButtonBase with hover.
 * - When `onClick` is omitted: renders as static Box (read-only).
 */

const baseSx: SxProps<Theme> = {
  display: 'inline-flex',
  alignItems: 'center',
  minHeight: 32,
  py: '3px',
  px: 1.5,
  borderRadius: 4,
  bgcolor: 'grey.100',
  border: 1,
  borderColor: 'grey.300',
  minWidth: 0,
};

interface AVPillProps {
  children: ReactNode;
  onClick?: () => void;
  sx?: SxProps<Theme>;
  'data-testid'?: string;
}

export const AVPill = ({ children, onClick, sx, 'data-testid': testId }: AVPillProps) => {
  if (onClick) {
    return (
      <ButtonBase
        data-testid={testId}
        onClick={onClick}
        sx={[
          baseSx,
          { cursor: 'pointer', '&:hover': { bgcolor: 'grey.300' } },
          ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
        ]}
      >
        {children}
      </ButtonBase>
    );
  }

  return (
    <Box
      data-testid={testId}
      sx={[baseSx, { cursor: 'default' }, ...(Array.isArray(sx) ? sx : sx ? [sx] : [])]}
    >
      {children}
    </Box>
  );
};
